'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { suggestSearchFromPath } from 'helpers/not-found-suggestion';
import { useGTM } from 'frontastic';
import CtaChevron from './cta-chevron';

// The part of the 404 page that depends on the URL the visitor actually hit. Next.js does not pass
// params to not-found.tsx, and the page is client-rendered anyway: notFound() thrown from a page
// escapes the server render (vercel/next.js#99287), so the server sends an empty shell and the
// real markup only appears on hydration. That makes a client component the one place the dead URL
// is available — and also why the page's metadata title is lost on hydration and reset here.
const NotFoundRecovery = () => {
  const pathname = usePathname();
  const { error404Event } = useGTM();

  useEffect(() => {
    document.title = 'Page Not Found | Viewrail';
    error404Event(undefined, pathname);
  }, [error404Event, pathname]);

  const { term, isProduct } = suggestSearchFromPath(pathname);
  if (!term) return null;

  const searchHref = `/search/?q=${encodeURIComponent(term)}`;

  return (
    <div className="mt-24 border-l-2 border-[#212121] pl-16 md:mt-32">
      <p className="text-14 leading-[1.5] text-[#5e5e5e]">
        {isProduct
          ? 'That product link points at an item we no longer list. The current version is usually a search away:'
          : 'Based on the address you tried, this may be what you were after:'}
      </p>
      <a href={searchHref} className="mt-8 flex min-h-44 items-center font-gt-s text-14 font-bold text-[#191919] hover:underline">
        Search for “{term}”
        <CtaChevron />
      </a>
    </div>
  );
};

export default NotFoundRecovery;
